// 定义插件
const UpdateDetector = {
  install(Vue, options = {}) {
    // 验证 Vue 实例
    if (!Vue) {
      console.error('UpdateDetector: Vue is required');
      return;
    }

    const defaultOptions = {
      intervalTime: 60000, // 默认检查间隔时间：60秒
      message: '新版本已经发布，请刷新页面获取最新版本！',
      autoCheck: true, // 是否自动检查
      onUpdate: null, // 更新回调函数
      // 添加新的配置选项
      maxRetries: 3, // 获取文件失败时的重试次数
      retryDelay: 3000, // 重试间隔时间（毫秒）
    };

    // 验证配置项
    if (options.intervalTime && options.intervalTime < 5000) {
      console.warn('UpdateDetector: intervalTime should not be less than 5000ms');
      options.intervalTime = 5000;
    }

    // 合并配置
    const config = { ...defaultOptions, ...options };
    let timer = null;
    let oldFiles = null;
    let retryCount = 0;

    // 检查更新函数
    async function checkForUpdates() {
      try {
        const newFiles = await getFilesName();

        if (!oldFiles) {
          oldFiles = newFiles;
          return;
        }

        const hasNewScripts = Array.from(newFiles.scriptsList)
          .some(script => !oldFiles.scriptsList.has(script));

        const hasNewStyles = Array.from(newFiles.linksList)
          .some(link => !oldFiles.linksList.has(link));

        if (hasNewScripts || hasNewStyles) {
          clearInterval(timer);

          // 如果提供了自定义更新回调，则使用它
          if (typeof config.onUpdate === 'function') {
            config.onUpdate();
          } else {
            const result = confirm(config.message);
            if (result) {
              window.location.reload();
            }
          }
        }
      } catch (error) {
        console.error('检查更新时出错:', error);
      }
    }

    // 获取文件名函数增加重试机制
    async function getFilesName() {
      try {
        const response = await fetch("./index.html");
        if (!response.ok) {
          throw new Error('获取 index.html 失败');
        }
        const htmlContent = await response.text();
        retryCount = 0; // 重置重试计数

        return {
          scriptsList: new Set(getScriptUrls(htmlContent)),
          linksList: new Set(getLinkUrls(htmlContent))
        };
      } catch (error) {
        console.error('获取文件列表失败:', error);
        if (retryCount < config.maxRetries) {
          retryCount++;
          console.log(`重试第 ${retryCount} 次...`);
          await new Promise(resolve => setTimeout(resolve, config.retryDelay));
          return getFilesName();
        }
        throw error;
      }
    }

    // 解析脚本URL
    function getScriptUrls(htmlContent) {
      const scriptUrls = [];
      const scriptTags = htmlContent.match(/<script[^>]*src="([^"]*)"[^>]*>/g) || [];
      for (const tag of scriptTags) {
        const match = tag.match(/src="([^"]*)"/);
        if (match && match[1] && !match[1].includes("?") && match[1].endsWith(".js")) {
          scriptUrls.push(match[1].split("/").pop());
        }
      }
      return scriptUrls;
    }

    // 解析样式URL
    function getLinkUrls(htmlContent) {
      const linkUrls = [];
      const linkTags = htmlContent.match(/<link[^>]*href="([^"]*)"[^>]*>/g) || [];
      for (const tag of linkTags) {
        const match = tag.match(/href="([^"]*)"/);
        if (match && match[1] && !match[1].includes("?") && match[1].endsWith(".css")) {
          linkUrls.push(match[1].split("/").pop());
        }
      }
      return linkUrls;
    }

    // 初始化检测器
    function initializeDetector() {
      getFilesName().then(files => {
        oldFiles = files;
        if (config.autoCheck) {
          timer = setInterval(checkForUpdates, config.intervalTime);
        }
      }).catch(error => {
        console.error('初始化更新检测器失败:', error);
      });
    }

    // 提供手动检查方法
    Vue.prototype.$checkUpdate = checkForUpdates;

    // 自动开始检测
    initializeDetector();

    // 添加销毁方法
    const destroyDetector = () => {
      if (timer) {
        clearInterval(timer);
        timer = null;
      }
    };

    // 将销毁方法添加到 Vue 实例
    Vue.prototype.$destroyUpdateDetector = destroyDetector;

    // 在 Vue 实例销毁时清理定时器
    if (Vue.version.startsWith('2.')) {
      Vue.mixin({
        beforeDestroy() {
          destroyDetector();
        }
      });
    }
  }
};

// 导出插件
export default UpdateDetector;
